import { useState, useEffect } from 'react';
import { AlertTriangle, HelpCircle, X, ShieldAlert } from 'lucide-react';

interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'warning' | 'danger' | 'high_stakes';
  /**
   * Konsekvens-liste vises som punkter under hovedmelding. Brukes med variant='high_stakes'
   * for å gjøre brukeren eksplisitt klar over hva som skjer.
   */
  consequences?: string[];
  /**
   * Hvis satt, må brukeren skrive dette ordet (case-insensitive) for å aktivere bekreft-knappen.
   * Standard for high_stakes: 'BEKREFT'. Sett til null/undefined for ingen tekst-bekreftelse.
   */
  requireTypedConfirmation?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Gjenbrukbar bekreftelses-dialog. Erstatter native window.confirm() —
 * gir tydeligere UX og hindrer utilsiktede klikk.
 *
 * variant='high_stakes' er for ikke-reversible endringer med stor konsekvens
 * (bytte av studieforbund, deaktivering av organisasjoner/brukere). Krever at
 * brukeren skriver et bekreftelsesord for å aktivere bekreft-knappen.
 */
export default function ConfirmDialog({
  open, title, message, confirmLabel = 'Bekreft', cancelLabel = 'Avbryt',
  variant = 'default', consequences = [], requireTypedConfirmation,
  onConfirm, onCancel
}: Props) {
  const [typed, setTyped] = useState('');

  // Reset typed-feltet hver gang dialogen åpnes
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  if (!open) return null;

  const variantStyles = {
    default:     { icon: HelpCircle,  iconColor: 'text-kf-primary', btn: 'bg-kf-primary hover:bg-kf-primary-dark', border: 'border-gray-200' },
    warning:     { icon: AlertTriangle, iconColor: 'text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700', border: 'border-amber-200' },
    danger:      { icon: AlertTriangle, iconColor: 'text-red-600',   btn: 'bg-red-600 hover:bg-red-700',       border: 'border-red-200' },
    high_stakes: { icon: ShieldAlert,   iconColor: 'text-red-700',   btn: 'bg-red-600 hover:bg-red-700',       border: 'border-red-300' }
  };
  const style = variantStyles[variant];
  const Icon = style.icon;
  const isHighStakes = variant === 'high_stakes';

  // High-stakes krever default typed-bekreftelse 'BEKREFT' hvis ikke eksplisitt overstyrt
  const typedTarget =
    requireTypedConfirmation === null ? null
    : requireTypedConfirmation !== undefined ? requireTypedConfirmation
    : (isHighStakes ? 'BEKREFT' : null);

  const typedOk = !typedTarget || typed.trim().toUpperCase() === typedTarget.toUpperCase();

  // High-stakes = wider modal (max-w-lg vs max-w-md) for å få plass til konsekvens-liste
  const widthClass = isHighStakes ? 'max-w-lg' : 'max-w-md';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
    >
      <div
        className={`bg-white rounded-lg shadow-xl ${widthClass} w-full border-2 ${style.border}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 ${style.iconColor}`}>
              <Icon className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{message}</p>

              {consequences.length > 0 && (
                <div className={`mt-4 p-3 rounded-md border ${isHighStakes ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${isHighStakes ? 'text-red-800' : 'text-amber-800'}`}>
                    Hvorfor du bør være helt sikker:
                  </p>
                  <ul className={`mt-2 space-y-1.5 text-sm ${isHighStakes ? 'text-red-900' : 'text-amber-900'}`}>
                    {consequences.map((c, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 mt-1">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {typedTarget && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Skriv <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs text-red-700">{typedTarget}</code> for å bekrefte:
                  </label>
                  <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoFocus
                    autoComplete="off"
                    spellCheck={false}
                    className={`w-full px-3 py-2 border rounded-md text-sm font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      typedOk && typed ? 'border-green-400 bg-green-50' : 'border-gray-300'
                    }`}
                    placeholder={typedTarget}
                  />
                </div>
              )}
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-900 flex-shrink-0"
              aria-label="Lukk"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded font-medium"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={!typedOk}
            className={`px-4 py-2 text-sm text-white rounded font-medium ${style.btn} disabled:opacity-40 disabled:cursor-not-allowed`}
            autoFocus={!typedTarget}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
